import React from 'react';
import type { TrackingDisplayDTO, TrackInfoItemDTO, InputChangeItemDTO } from '../api/kfc';

const TRUCK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

interface TrackingDisplayPanelProps {
  /** 轨迹展示数据（来自 GET /shipments/:id/tracking-display） */
  data: TrackingDisplayDTO | null | undefined;
  /** 无 data 时的兜底展示（原 agentTrackingLatest 格式） */
  fallbackEvents?: { time?: string; location?: string; description?: string }[];
  loading?: boolean;
}

/** 轨迹时间线单条：支持不同代理字段（时间、状态、地点、描述） */
function TrackTimelineItem({
  item,
  isLatest,
  isLast,
}: {
  item: TrackInfoItemDTO;
  isLatest: boolean;
  isLast: boolean;
}) {
  const time = item.trackTime ?? '';
  const status = item.trackStatusText ?? '';
  const location = [item.trackStation, item.trackStationEn].filter(Boolean).join(' ') || '-';
  const desc = item.trackDesc ?? item.trackDescEn ?? '';

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: isLatest ? '#1890ff' : '#d9d9d9',
            flexShrink: 0,
          }}
        />
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e8e8e8', marginTop: 4 }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: 4 }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{time}</div>
        <div style={{ fontWeight: 500, marginBottom: 2 }}>
          {status && <span style={{ marginRight: 8 }}>{status}</span>}
          {location && <span style={{ color: '#666' }}>{location}</span>}
        </div>
        {desc && <div style={{ fontSize: 12, color: '#666' }}>{desc}</div>}
      </div>
    </div>
  );
}

/** 入库信息时间线单条 */
function InputChangeTimelineItem({
  item,
  isLatest,
  isLast,
}: {
  item: InputChangeItemDTO;
  isLatest: boolean;
  isLast: boolean;
}) {
  const time = item.createTime ?? '';
  const busiCode = item.busiCode ?? '';

  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: isLast ? 0 : 16 }}>
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: isLatest ? '#1890ff' : '#d9d9d9',
            flexShrink: 0,
          }}
        />
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e8e8e8', marginTop: 4 }} />}
      </div>
      <div style={{ flex: 1, paddingBottom: 4 }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>{time}</div>
        <div style={{ fontWeight: 500 }}>{busiCode}</div>
      </div>
    </div>
  );
}

const TrackingDisplayPanel: React.FC<TrackingDisplayPanelProps> = ({
  data,
  fallbackEvents,
  loading,
}) => {
  if (loading) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
        加载轨迹中…
      </div>
    );
  }

  const hasRich = data && (data.trackInfoList?.length || data.inputChanges?.length || data.serviceName != null || data.latestTime != null);
  const hasFallback = fallbackEvents && fallbackEvents.length > 0;

  if (!hasRich && !hasFallback) {
    return (
      <div style={{ color: '#999', fontSize: 12 }}>
        暂无轨迹，点击「同步轨迹数据」从代理拉取
      </div>
    );
  }

  // 富展示：头部 + 转运信息 + 入库信息
  if (hasRich && data) {
    const trackList = data.trackInfoList ?? [];
    const inputChanges = data.inputChanges ?? [];

    return (
      <div style={{ fontSize: 12 }}>
        {/* 头部：单号/状态 + 渠道、时间、描述 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 20,
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#666' }}>{TRUCK_ICON}</span>
            <span>
              <span style={{ fontWeight: 500 }}>{data.userNo ?? '-'}/</span>
              <span style={{ color: '#666', marginLeft: 4 }}>{data.latestStatus ?? '-'}</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 24, color: '#666' }}>
            <div>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>渠道</div>
              <div>{data.serviceName ?? '-'}</div>
            </div>
            <div>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>时间</div>
              <div>{data.latestTime ?? '-'}</div>
            </div>
            <div style={{ maxWidth: 200 }}>
              <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>描述</div>
              <div style={{ wordBreak: 'break-word' }}>{data.latestDetail ?? '-'}</div>
            </div>
          </div>
        </div>

        {/* 转运信息 */}
        {trackList.length > 0 && (
          <>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>转运信息</h4>
            <div style={{ paddingLeft: 4 }}>
              {trackList.map((item, index) => (
                <TrackTimelineItem
                  key={index}
                  item={item}
                  isLatest={index === 0}
                  isLast={index === trackList.length - 1}
                />
              ))}
            </div>
          </>
        )}

        {/* 入库信息 */}
        {inputChanges.length > 0 && (
          <>
            <h4 style={{ margin: '20px 0 12px', fontSize: 13, fontWeight: 600 }}>入库信息</h4>
            <div style={{ paddingLeft: 4 }}>
              {inputChanges.map((item, index) => (
                <InputChangeTimelineItem
                  key={index}
                  item={item}
                  isLatest={index === 0}
                  isLast={index === inputChanges.length - 1}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // 兜底：仅用 fallbackEvents 展示简单时间线（与原有列表结构一致，但用时间线样式）
  if (hasFallback && fallbackEvents) {
    return (
      <div style={{ fontSize: 12 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>转运信息</h4>
        <div style={{ paddingLeft: 4 }}>
          {fallbackEvents.map((evt, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: i === 0 ? '#1890ff' : '#d9d9d9',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <div>
                <span style={{ color: '#666' }}>{evt.time ?? '-'}</span>
                {evt.location && ` · ${evt.location}`}
                {evt.description && ` · ${evt.description}`}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ color: '#999', fontSize: 12 }}>暂无轨迹</div>
  );
};

export default TrackingDisplayPanel;
